/*
 * Copyright 2020 Spotify AB
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { JsonObject, JsonValue } from '@backstage/config';
import {
  ApiHolder,
  errorApiRef,
  githubAuthApiRef,
  useApi,
  useApiHolder,
  useRouteRef,
} from '@backstage/core-plugin-api';
import {Grid, LinearProgress, makeStyles, Typography} from '@material-ui/core';
import { FormValidation, IChangeEvent } from '@rjsf/core';
import React, { useCallback, useState } from 'react';
import { generatePath, Navigate, useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { useAsync } from 'react-use';
import { scaffolderApiRef, ScaffolderScaffoldOptions } from '../../api';
import { CustomFieldValidator, FieldExtensionOptions } from '../../extensions';
import { rootRouteRef } from '../../routes';
import { MultistepJsonForm } from '../MultistepJsonForm';
import {
  PageLayout,
  SupportButton,
  Content,
  SupportTextList,
  SupportLinks,
  SupportMultipleLinkCard
} from 'tmna-ui-kit';
import DescriptionIcon from '@material-ui/icons/Description';

const useStyles = makeStyles(theme => ({
  content: {
    paddingTop: theme.spacing(5),
  }
}));

const RELATED_DOCS_SUPPORT_LINKS = [
  {
    to: '/docs/default/Component/ACE-TECH-DOCS/blueprints/definition/',
    title: 'Get the concept of the Blueprints',
  },
  {
    to: '/training/detail/how-to-request-a-cloud-account',
    title: 'How to request a Cloud Account',
  },
  {
    to: '/training/detail/how-to-set-up-your-cloud-account',
    title: 'How to set up a Cloud Account',
  }
];

const useTemplateParameterSchema = (templateName: string) => {
  const scaffolderApi = useApi(scaffolderApiRef);
  
  const { value, loading, error } = useAsync(
    () =>
      scaffolderApi.getTemplateParameterSchema({
        name: templateName,
        kind: 'template',
        namespace: 'default',
      }),
    [scaffolderApi, templateName],
  );  
  return { schema: value, loading, error };
};

function isObject(obj: unknown): obj is JsonObject {
  return typeof obj === 'object' && obj !== null && !Array.isArray(obj);
}

export const createValidator = (
    rootSchema: JsonObject,
    validators: Record<string, undefined | CustomFieldValidator<unknown>>,
    context: {
        apiHolder: ApiHolder;
    },
) => {
  function validate(
    schema: JsonObject,
    formData: JsonObject,
    errors: FormValidation,
  ) {
    const schemaProps = schema.properties;
    if (!isObject(schemaProps)) {
      return;
    }

    for (const [key, propData] of Object.entries(formData)) {
      const propValidation = errors[key];

      if (isObject(propData)) {
        const propSchemaProps = schemaProps[key];
        if (isObject(propSchemaProps)) {
          validate(
            propSchemaProps,
            propData as JsonObject,
            propValidation as FormValidation,
          );
        }
      } else {
        const propSchema = schemaProps[key];
        const fieldName =
          isObject(propSchema) && (propSchema['ui:field'] as string);
        if (fieldName && typeof validators[fieldName] === 'function') {
          validators[fieldName]!(
              propData as JsonValue,
              propValidation,
              context,
          );
        }
      }
    }
  }

  return (formData: JsonObject, errors: FormValidation) => {
    validate(rootSchema, formData, errors);
    return errors;
  };
};

export const TemplatePage = ({
  customFieldExtensions = [],
  selfService = false,
}: {
  customFieldExtensions?: FieldExtensionOptions[];
  selfService?:boolean;
}) => {
  const apiHolder = useApiHolder();
  const classes = useStyles();
  const errorApi = useApi(errorApiRef);
  const scaffolderApi = useApi(scaffolderApiRef);
  const githubAuthApi = useApi(githubAuthApiRef);
  const { templateName } = useParams();
  const navigate = useNavigate();
  const rootLink = useRouteRef(rootRouteRef);
  const { schema, loading, error } = useTemplateParameterSchema(templateName as any);
  const [formState, setFormState] = useState({});
  const handleFormReset = () => setFormState({});

  const handleChange = useCallback(
      (e: IChangeEvent) => setFormState(e.formData),
      [setFormState],
  );

  const templateObject: ScaffolderScaffoldOptions = {
    templateRef:templateName as any,
    values:formState,
  }

  const handleCreate = async () => {   
    try {
      const githubToken = await githubAuthApi.getAccessToken(['repo', 'workflow'])
      const id = await scaffolderApi.scaffold({...templateObject,secrets:{'TOKEN': githubToken },});
      let customPath = selfService ? '/self-service' : rootLink();
      navigate(generatePath(`${customPath}/tasks/:taskId`, { taskId: id.taskId }));
     
    } catch (e: any) {
      errorApi.post(e);
      console.log('error: ',  errorApi.post(e))
    }
  };

  const backToLink = React.useMemo(() => ({
    to: selfService ? '/self-service': rootLink(),
    label: selfService ? 'Back to Self Service': 'Back to Blueprints'
  }), [rootLink]);

  if (error) {
    errorApi.post(new Error(`Failed to load template, ${error}`));
    return <Navigate to={selfService ? '/self-service': rootLink()} />;
  }
  if (!loading && !schema) {
    errorApi.post(new Error('Template was not found.'));
    return <Navigate to={selfService ? '/self-service': rootLink()} />;
  }

  const customFieldComponents = Object.fromEntries(
      customFieldExtensions.map(({ name, component }) => [name, component]),
  );

  const customFieldValidators = Object.fromEntries(
      customFieldExtensions.map(({ name, validation }) => [name, validation]),
  );

  return (
    <PageLayout
        type="entity"
        title={schema?.title || ''}
        backToLink={backToLink}
    >
      <Content className={classes.content}>
        {loading && <LinearProgress data-testid="loading-progress" />}
        <Grid container direction="row" justifyContent="space-between">
          {schema && (
         
            <Grid item  style={{width:"85%"}}>
              <MultistepJsonForm
                formData={formState}
                fields={customFieldComponents}
                onChange={handleChange}
                onReset={handleFormReset}
                onFinish={handleCreate}
                steps={schema.steps.map(step => {                  
                  return {
                    ...step,
                    validate: createValidator(
                        step.schema,
                        customFieldValidators,
                        {apiHolder},
                    ),
                  };
                })}
              />
            </Grid>
          )}
          <Grid item>
            <SupportButton>
              <SupportTextList>
                <Typography variant="body2">
                  Enter and review the details for your component,
                  then start the automated creation process.
                </Typography>
                <Typography variant="body2">
                  Read and watch the materials below to learn the features and functions of the Template page.
                </Typography>
              </SupportTextList>
              <SupportLinks>
                <SupportMultipleLinkCard
                  icon={<DescriptionIcon />}
                  title="Related Docs"
                  items={RELATED_DOCS_SUPPORT_LINKS}
                />
              </SupportLinks>
            </SupportButton>
          </Grid>
        </Grid>
      </Content>
    </PageLayout>
  );
};
